// Built-in visual block components, rebuilt on the Bluepill design-system tokens
// (theme.ts). Everything reads CSS custom properties from the `.insight-root`
// wrapper, so the look is consistent and re-themeable per answer. Each block
// accepts an optional validated `style` (emphasis/tone/size). Defends against
// missing/partial props (the data is LLM-shaped).

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from './markdown';
import { StatItem, ThemeBarItem, QuoteItem, CalloutKind } from './types';
import { BlockStyle, Tone, TONES, isTone } from './theme';

// ── Shared primitive: a themed surface (card or flat, per the theme tokens) ──
const SHADOW: Record<NonNullable<BlockStyle['emphasis']>, string> = {
  subtle: 'none',
  normal: 'var(--insight-shadow)',
  strong: '0 2px 6px rgba(16,24,40,0.07), 0 12px 32px rgba(16,24,40,0.10)',
};

const Surface: React.FC<{ style?: BlockStyle; className?: string; children: React.ReactNode }> = ({
  style, className = '', children,
}) => (
  <div
    className={`rounded-[var(--insight-radius)] border bg-[var(--insight-card)] ${className}`}
    style={{
      borderColor: 'var(--insight-border)',
      boxShadow: style?.emphasis ? SHADOW[style.emphasis] : 'var(--insight-shadow)',
    }}
  >
    {children}
  </div>
);

const ACCENT_GRADIENT = 'linear-gradient(to right, var(--insight-accent), var(--insight-accent-2))';
const accentTint = (pct = 8) => `color-mix(in srgb, var(--insight-accent) ${pct}%, transparent)`;

// ── ShortAnswer ──
export const ShortAnswer: React.FC<{ text?: string }> = ({ text }) => {
  if (!text) return null;
  return (
    <div
      className="my-3 rounded-[var(--insight-radius)] border-l-[3px] px-4 py-3"
      style={{ borderColor: 'var(--insight-accent)', background: accentTint(8) }}
    >
      <p className="text-[15px] font-semibold leading-relaxed" style={{ color: 'var(--insight-ink)' }}>{text}</p>
    </div>
  );
};

// ── Prose ──
export const Prose: React.FC<{ markdown?: string }> = ({ markdown }) => {
  if (!markdown) return null;
  return (
    <div className="my-2">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{markdown}</ReactMarkdown>
    </div>
  );
};

// ── StatGrid ──
const STAT_SIZE: Record<NonNullable<BlockStyle['size']>, string> = {
  sm: 'text-xl', md: 'text-[28px]', lg: 'text-4xl',
};
export const StatGrid: React.FC<{ items?: StatItem[]; style?: BlockStyle }> = ({ items, style }) => {
  const list = (items || []).filter((s) => s && (s.value || s.label));
  if (list.length === 0) return null;
  const valueSize = STAT_SIZE[style?.size || 'md'];
  return (
    <div className="my-3 grid grid-cols-2 md:grid-cols-3 gap-[var(--insight-gap)]">
      {list.map((s, i) => (
        <Surface key={i} style={style} className="relative overflow-hidden p-[var(--insight-pad)] transition-shadow">
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: ACCENT_GRADIENT }} />
          <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--insight-muted)' }}>{s.label}</p>
          <p className={`mt-2 font-bold tracking-tight leading-none tabular-nums ${valueSize}`} style={{ color: 'var(--insight-ink)' }}>{s.value}</p>
          {s.sublabel && <p className="mt-1.5 text-xs" style={{ color: 'var(--insight-muted)' }}>{s.sublabel}</p>}
        </Surface>
      ))}
    </div>
  );
};

// ── ThemeBars ──
export const ThemeBars: React.FC<{ items?: ThemeBarItem[]; style?: BlockStyle }> = ({ items, style }) => {
  const list = (items || [])
    .map((t) => (t ? { ...t, pct: Number(t.pct) } : t))
    .filter((t): t is ThemeBarItem => !!t && Number.isFinite(t.pct));
  if (list.length === 0) return null;
  return (
    <Surface style={style} className="my-3 p-[var(--insight-pad)]">
      <div className="space-y-4">
        {list.map((t, i) => {
          const pct = Math.max(0, Math.min(100, t.pct));
          return (
            <div key={i}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-sm font-medium" style={{ color: 'var(--insight-ink)' }}>{t.label}</span>
                <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--insight-ink)' }}>
                  {pct}%
                  {t.n != null && t.of != null && (
                    <span className="ml-1.5 font-normal" style={{ color: 'var(--insight-muted)' }}>{t.n}/{t.of}</span>
                  )}
                </span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: accentTint(14) }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ACCENT_GRADIENT }} />
              </div>
            </div>
          );
        })}
      </div>
    </Surface>
  );
};

// ── QuoteCard / QuoteGroup ──
export const QuoteCard: React.FC<QuoteItem> = ({ quote, persona, segment }) => {
  if (!quote) return null;
  const attribution = [persona, segment].filter(Boolean).join(' — ');
  return (
    <Surface className="my-3 p-[var(--insight-pad)]">
      <figure className="m-0">
        <blockquote
          className="border-l-[3px] pl-3 text-sm italic leading-relaxed m-0"
          style={{ borderColor: 'var(--insight-accent)', color: 'var(--insight-ink)' }}
        >
          “{quote}”
        </blockquote>
        {attribution && (
          <figcaption className="mt-2 flex items-center gap-2">
            {persona && (
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
                style={{ background: ACCENT_GRADIENT }}
              >
                {persona.trim().charAt(0).toUpperCase()}
              </span>
            )}
            <span className="text-xs font-medium" style={{ color: 'var(--insight-muted)' }}>{attribution}</span>
          </figcaption>
        )}
      </figure>
    </Surface>
  );
};

export const QuoteGroup: React.FC<{ quotes?: QuoteItem[] }> = ({ quotes }) => {
  const list = (quotes || []).filter((q) => q && q.quote);
  if (list.length === 0) return null;
  return <div className="my-2 space-y-2">{list.map((q, i) => <QuoteCard key={i} {...q} />)}</div>;
};

// ── Callout ──
const KIND_LABEL: Record<CalloutKind, string> = { tension: 'The tension', insight: 'Key insight', note: 'Note' };
export const Callout: React.FC<{ kind?: CalloutKind; text?: string; style?: BlockStyle }> = ({ kind, text, style }) => {
  if (!text) return null;
  const k: CalloutKind = kind && KIND_LABEL[kind] ? kind : 'note';
  // Colors: an explicit per-block tone overrides; otherwise derive from the kind
  // (insight = accent, tension = caution/amber, note = neutral). The label always
  // reflects the semantic kind.
  const toneOverride: Tone | null = isTone(style?.tone) && style!.tone !== 'default' ? (style!.tone as Tone) : null;
  let bg: string, border: string, labelColor: string;
  if (toneOverride) {
    bg = TONES[toneOverride].bg; border = TONES[toneOverride].border; labelColor = TONES[toneOverride].fg;
  } else if (k === 'insight') {
    bg = accentTint(8); border = 'var(--insight-accent)'; labelColor = 'var(--insight-accent)';
  } else if (k === 'tension') {
    bg = TONES.caution.bg; border = TONES.caution.border; labelColor = TONES.caution.fg;
  } else {
    bg = 'var(--insight-bg)'; border = 'var(--insight-border)'; labelColor = 'var(--insight-muted)';
  }
  return (
    <div className="my-3 rounded-[var(--insight-radius)] border-l-4 px-4 py-3" style={{ background: bg, borderColor: border }}>
      <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: labelColor }}>{KIND_LABEL[k]}</p>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--insight-ink)' }}>{text}</p>
    </div>
  );
};

// ── ComparisonTable ──
export const ComparisonTable: React.FC<{ columns?: string[]; rows?: string[][]; style?: BlockStyle }> = ({ columns, rows, style }) => {
  const cols = columns || [];
  const data = rows || [];
  if (cols.length === 0 || data.length === 0) return null;
  return (
    <Surface style={style} className="my-3 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead style={{ background: accentTint(6) }}>
          <tr>
            {cols.map((c, i) => (
              <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--insight-muted)' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} style={{ borderTop: '1px solid var(--insight-border)' }}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-4 py-2.5 text-sm align-top" style={{ color: 'var(--insight-ink)' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Surface>
  );
};

// ── BulletList ──
export const BulletList: React.FC<{ items?: string[] }> = ({ items }) => {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <ul className="my-2 ml-1 space-y-1.5">
      {list.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm leading-relaxed" style={{ color: 'var(--insight-ink)' }}>
          <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: 'var(--insight-accent)' }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};
